/**
 * What a station makes, and what somebody has told it to make by default.
 *
 * The recipes come from the server with the rest of the definitions, so a
 * crafting window is built from what the world actually holds rather than from a
 * list written twice. What is set as a default is a preference and lives in the
 * browser: two people at the same anvil want different things from it.
 */

import type { ItemStack } from '@inverse/common';
import type { ItemDefinitionWire, RecipeWire } from '@inverse/protocol';

/** A default: what this kind of station makes on a left click, and how many. */
export interface StationDefault {
  action: string;
  /** True to keep going until the materials run out, false for one at a time. */
  all: boolean;
}

export type StationDefaults = Record<number, StationDefault>;

const STORAGE_KEY = 'inverse.stations';

export function loadDefaults(): StationDefaults {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StationDefaults) : {};
  } catch {
    return {};
  }
}

export function saveDefaults(defaults: StationDefaults): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
  } catch {
    // Storage is unavailable inside some embeds. The choice lasts this session.
  }
}

/**
 * The count somebody typed into the crafting window, by kind of station.
 *
 * Kept beside the defaults and for the same reason: how many bars a person
 * makes at a time is a habit of theirs, not a fact about the anvil.
 */
const COUNTS_KEY = 'inverse.craftCounts';

export function loadCounts(): Record<number, number> {
  try {
    const raw = window.localStorage.getItem(COUNTS_KEY);
    return raw ? (JSON.parse(raw) as Record<number, number>) : {};
  } catch {
    return {};
  }
}

export function saveCount(objectId: number, count: number): void {
  try {
    const held = loadCounts();
    held[objectId] = count;
    window.localStorage.setItem(COUNTS_KEY, JSON.stringify(held));
  } catch {
    // Storage is unavailable inside some embeds. The number lasts this session.
  }
}

/**
 * Whether a recipe is known at all, and what it is still short of.
 *
 * Two different kinds of requirement, answered differently. A level is a thing
 * somebody is working towards, so a recipe out of reach is shown greyed with
 * the level on it: that is what the next levels are for. A quest or a flag is
 * not a number anybody can see coming, so a recipe behind one is not shown at
 * all until it is learned, and appears the moment it is.
 */
export interface RecipeState {
  /** Nothing has taught them this yet. */
  unknown: boolean;
  /** Skills it wants that are not there yet, the trained one included. */
  short: { skill: string; level: number; have: number }[];
}

export function recipeState(
  recipe: RecipeWire,
  levels: Readonly<Record<string, number>>,
  progress: {
    quests: readonly { id: string; step: number }[];
    flags: readonly { name: string; value: string }[];
  },
): RecipeState {
  const short: RecipeState['short'] = [];

  for (const need of [{ skill: recipe.skill, level: recipe.level }, ...recipe.alsoNeeds]) {
    const have = levels[need.skill] ?? 1;
    if (have < need.level) short.push({ skill: need.skill, level: need.level, have });
  }

  let unknown = false;
  const gate = recipe.locked;

  if (gate) {
    if (gate.quest) {
      const along = progress.quests.find((entry) => entry.id === gate.quest)?.step ?? 0;
      if (along < Math.max(1, gate.atLeast)) unknown = true;
    }

    if (gate.flag) {
      const held = progress.flags.find((entry) => entry.name === gate.flag)?.value ?? '';
      const matches = gate.is.length > 0 ? held === gate.is : held.length > 0;
      if (!matches) unknown = true;
    }
  }

  return { unknown, short };
}

/** Everything one station makes. */
export function recipesAt(recipes: readonly RecipeWire[], objectId: number): RecipeWire[] {
  return recipes.filter((recipe) => recipe.objectId === objectId);
}

/** How many of a recipe the bag has the materials for. */
export function couldMake(recipe: RecipeWire, bag: readonly (ItemStack | null)[]): number {
  let held = 0;
  for (const stack of bag) {
    if (stack && stack.id === recipe.inputId) held += stack.quantity;
  }

  return Math.floor(held / Math.max(1, recipe.inputQuantity));
}

/**
 * What the left click on a station says, given what it has been told to make.
 *
 * "Smelt 4 Copper bars" when there are four bars' worth of ore in the bag, and
 * the plain "Smelt" when there is nothing to make: an entry that promises
 * nothing is worse than one that opens the window and shows why.
 */
export function defaultLabel(
  recipe: RecipeWire,
  bag: readonly (ItemStack | null)[],
  items: readonly ItemDefinitionWire[],
  all: boolean,
): string | null {
  const many = couldMake(recipe, bag);
  if (many <= 0) return null;

  const made = items.find((item) => item.id === recipe.outputId);
  const name = made?.name ?? `Item ${recipe.outputId}`;
  const count = all ? many : 1;

  return `${recipe.action} ${count} ${count === 1 ? name : plural(name)}`;
}

/** A plural that is right often enough for a menu entry. */
export function plural(name: string): string {
  if (/(s|x|z|ch|sh)$/i.test(name)) return `${name}es`;
  return `${name}s`;
}
